import type {
  BuildingGraphFaceNodeSideHandle,
  BuildingGraphFaceSideHandle,
  BuildingGraphWallPlaneSideHandle,
} from "building-types/buildingGraph/buildingGraphSideHandles";
import type { BuildingGraphFilterBuilding } from "building-types/buildingGraphFilter/buildingGraphFilterBuilding";
import type { RowForSource } from "building-types/buildingGraphFilter/entitySource";
import type {
  DocBuildingGraphFilterGraph,
  DocFaceDirection,
  DocFaceNodeSubFilter,
  DocWallPlaneFilter,
} from "building-types/walls2/wallPlaneFilters";
import type { WallPlaneAnchoredRuleEntity } from "building-types/walls2/wallPlaneRules";
import { WindingDir } from "geom/core";
import { executeDocBuildingGraphFilter } from "./buildingGraphFilterEngine";
import { faceNodeGetTopologyFaceIds } from "./entityFilterSelectors";

export type PrecomputedTopLevelMatches =
  | {
      readonly targetType: "WALL_PLANE";
      readonly matches: ReadonlyMap<string, RowForSource["WALL_PLANE"]>;
    }
  | {
      readonly targetType: "FACE_NODE";
      readonly matches: ReadonlyMap<string, RowForSource["FACE_NODE"]>;
    };

export interface FaceNodeProjectionConfig {
  readonly direction: DocFaceDirection;
  readonly subFilter: DocFaceNodeSubFilter;
  readonly filterGraphs: Record<string, DocBuildingGraphFilterGraph>;
  readonly operationId: string;
}

/**
 * Projects matched FACE_NODE rows into typed face-side handles using direction and subFilter.
 */
export function* executeFaceNodeProjection(
  config: FaceNodeProjectionConfig,
  faceNodes: ReadonlyMap<string, RowForSource["FACE_NODE"]>,
  building: BuildingGraphFilterBuilding,
): Iterable<BuildingGraphFaceSideHandle> {
  const baseHandles: BuildingGraphFaceNodeSideHandle[] = [];
  for (const faceNode of faceNodes.values()) {
    baseHandles.push({
      type: "FACE_NODE_SIDE",
      faceSource: { type: "FACE_NODE", faceNodeId: faceNode.faceNodeId },
      faceDirection: config.direction,
    });
  }

  switch (config.subFilter.kind) {
    case "UNDEFINED":
      yield* baseHandles;
      return;
    case "PERIMETER":
      yield* projectPerimeterTopologyFaceSides(baseHandles, building);
      return;
    case "OPPOSITE":
      yield* projectOppositeFaceEdgeSides(baseHandles, config, building);
      return;
  }
}

/**
 * Executes a catalog wall face filter: evaluate FACE_NODE predicates, then project to face-side handles.
 */
export function* executeWallFaceCatalogFilter(
  filter: Pick<DocWallPlaneFilter, "filterGraphs" | "filterGraphId" | "id" | "subFilter">,
  building: BuildingGraphFilterBuilding,
  direction: DocFaceDirection,
  precomputedTopLevelMatches?: PrecomputedTopLevelMatches,
): Iterable<BuildingGraphFaceSideHandle> {
  const faceNodes = evaluateFaceNodeTopLevelMatches(filter, building, precomputedTopLevelMatches);
  yield* executeFaceNodeProjection(
    {
      direction,
      subFilter: filter.subFilter,
      filterGraphs: filter.filterGraphs,
      operationId: filter.id,
    },
    faceNodes,
    building,
  );
}

export function* executeWallPlaneAnchoredRule(
  rule: WallPlaneAnchoredRuleEntity,
): Iterable<BuildingGraphWallPlaneSideHandle> {
  yield {
    type: "WALL_PLANE_SIDE",
    faceSource: { type: "WALL_PLANE", planeHash: rule.planeHash },
    faceDirection: rule.direction,
  };
}

export function evaluateFaceNodeTopLevelMatches(
  filter: Pick<DocWallPlaneFilter, "filterGraphs" | "filterGraphId" | "id">,
  building: BuildingGraphFilterBuilding,
  precomputedTopLevelMatches?: PrecomputedTopLevelMatches,
): Map<string, RowForSource["FACE_NODE"]> {
  const precomputedMatches =
    precomputedTopLevelMatches?.targetType === "FACE_NODE" ? precomputedTopLevelMatches.matches : undefined;
  if (precomputedMatches) {
    return new Map(precomputedMatches);
  }
  return executeDocBuildingGraphFilter("FACE_NODE", filter.filterGraphs, filter.filterGraphId, building, filter.id);
}

function* projectPerimeterTopologyFaceSides(
  baseHandles: readonly BuildingGraphFaceNodeSideHandle[],
  building: BuildingGraphFilterBuilding,
): Iterable<BuildingGraphFaceSideHandle> {
  const seen = new Set<string>();
  for (const base of baseHandles) {
    const faceNodeId = base.faceSource.faceNodeId;
    for (const topologyFaceId of faceNodeGetTopologyFaceIds(faceNodeId, building)) {
      const colonIndex = topologyFaceId.lastIndexOf(":");
      if (colonIndex < 0) {
        continue;
      }
      const topologyId = topologyFaceId.slice(0, colonIndex);
      const faceIndex = Number(topologyFaceId.slice(colonIndex + 1));
      if (!Number.isInteger(faceIndex)) {
        continue;
      }
      const topology = building.buildingGraph.faceTopologies[topologyId];
      if (!topology) {
        continue;
      }
      if (!isTopologyFacePerimeter(topology, faceIndex, building)) {
        continue;
      }
      const dedupeKey = `${topologyFaceId}#${faceNodeId}#${base.faceDirection}`;
      if (seen.has(dedupeKey)) {
        continue;
      }
      seen.add(dedupeKey);
      yield {
        type: "TOPOLOGY_FACE_SIDE",
        faceSource: { type: "TOPOLOGY_FACE", topologyId, faceIndex },
        faceSide: base,
      };
    }
  }
}

function isTopologyFacePerimeter(
  topology: NonNullable<BuildingGraphFilterBuilding["buildingGraph"]["faceTopologies"][string]>,
  faceIndex: number,
  building: BuildingGraphFilterBuilding,
): boolean {
  if (topology.planeTopology.planarGraph.faceWindingDirs[faceIndex] === WindingDir.CW) {
    return false;
  }
  const faceEdgeIds = topology.planarGraphFaceToFaceEdgeIds[faceIndex] ?? [];
  for (const edgeIndex of faceEdgeIds) {
    const edge = building.buildingGraph.faceEdges[edgeIndex];
    // Same-direction OVERLAP (adjacent rooms on one façade) stays perimeter.
    // Opposing OVERLAP (through-wall interior) is non-perimeter.
    if (edge?.faceEdgeType === "OVERLAP" && edge.faceEdgeDot < 0) {
      return false;
    }
  }
  return true;
}

/**
 * OPPOSITE means through-wall: only OVERLAP edges whose normals oppose (faceEdgeDot < 0).
 * Same-direction OVERLAP (adjacent rooms on one façade) and EDGE adjacency are excluded.
 *
 * When both endpoints of a through-wall edge match the top-level filter, yield once per
 * base face (not once per edge). Edge-only dedupe would attribute the handle to whichever
 * side was visited first and drop the other room's selection.
 */
function* projectOppositeFaceEdgeSides(
  baseHandles: readonly BuildingGraphFaceNodeSideHandle[],
  config: FaceNodeProjectionConfig,
  building: BuildingGraphFilterBuilding,
): Iterable<BuildingGraphFaceSideHandle> {
  if (config.subFilter.kind !== "OPPOSITE") {
    return;
  }
  const oppositeMatchIds = collectOppositeFaceNodeIds(config.subFilter.oppositeFilterGraphId, config, building);
  const seen = new Set<string>();
  for (const base of baseHandles) {
    const baseId = base.faceSource.faceNodeId;
    const incident = building.buildingGraph.faceNodesToEdgeIds[baseId] ?? [];
    for (const edgeIndex of incident) {
      const edge = building.buildingGraph.faceEdges[edgeIndex];
      if (!edge || edge.faceEdgeType !== "OVERLAP" || edge.faceEdgeDot >= 0) {
        continue;
      }
      const oppositeId = edge.faceANodeId === baseId ? edge.faceBNodeId : edge.faceANodeId;
      if (!oppositeMatchIds.has(oppositeId)) {
        continue;
      }
      const dedupeKey = `${edgeIndex}#${baseId}#${base.faceDirection}`;
      if (seen.has(dedupeKey)) {
        continue;
      }
      seen.add(dedupeKey);
      yield {
        type: "FACE_EDGE_SIDE",
        faceSource: { type: "FACE_EDGE", faceEdgeIndex: edgeIndex },
        faceSide: base,
      };
    }
  }
}

function collectOppositeFaceNodeIds(
  oppositeFilterGraphId: string,
  config: FaceNodeProjectionConfig,
  building: BuildingGraphFilterBuilding,
): ReadonlySet<string> {
  const matches = executeDocBuildingGraphFilter(
    "FACE_NODE",
    config.filterGraphs,
    oppositeFilterGraphId,
    building,
    `${config.operationId}#opposite`,
  );
  const ids = new Set<string>();
  for (const row of matches.values()) {
    ids.add(row.faceNodeId);
  }
  return ids;
}
